import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { default as Plyr } from 'plyr';
import { twMerge } from 'tailwind-merge';

interface UseVideoPlayerProps {
  videoUrl?: string;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  videoClassName?: string;
  chapters?: VideoChapter[];
  // CSS selector for the ancestor of `.plyr` that should go fullscreen, so
  // overlay siblings (e.g. the detection canvas) stay visible in fullscreen.
  fullscreenContainer?: string;
  onLoadedMetadata?: () => void;
}

interface VideoChapter {
  startTime: number;
  endTime: number;
  title: string;
}

interface UseVideoPlayerChaptersProps {
  chapters?: VideoChapter[];
  plyrContainer: HTMLElement | null;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  videoUrl?: string;
}

export const useVideoPlayer = ({
  videoUrl,
  videoRef,
  videoClassName,
  chapters,
  fullscreenContainer,
  onLoadedMetadata,
}: UseVideoPlayerProps) => {
  const [plyrContainer, setPlyrContainer] = useState<HTMLElement | null>(null);
  const [isVideoBuffering, setIsVideoBuffering] = useState(!!videoUrl);
  const plyrRef = useRef<Plyr | null>(null);
  const playerHolderRef = useRef<HTMLDivElement>(null);

  // Pass chapters through a ref so the Plyr-init effect can read the current
  // list without re-running (and tearing down the player) when the parent
  // reuses a new array reference on every render.
  const chaptersRef = useRef(chapters);
  chaptersRef.current = chapters;

  // Manage the <video> element manually so React doesn't fight with Plyr.
  // Plyr's destroy() replaces its container with a clone of the original media
  // element, which would otherwise leave React's `videoRef` pointing at a
  // detached node (very visible under StrictMode's mount→cleanup→mount cycle).
  useEffect(() => {
    const holder = playerHolderRef.current;
    if (!holder || !videoUrl) return undefined;

    const forwardedRef = videoRef as MutableRefObject<HTMLVideoElement | null>;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.setAttribute('controlsList', 'nodownload');
    video.preload = 'metadata';
    holder.appendChild(video);
    forwardedRef.current = video;

    const markerPoints = (chaptersRef.current ?? [])
      .filter((chapter) => chapter.startTime > 0)
      .map((chapter) => ({
        time: chapter.startTime,
        label: chapter.title,
      }));

    const player = new Plyr(video, {
      controls: [
        // 'play-large',
        'play',
        'progress',
        'current-time',
        'duration',
        'fullscreen',
        // 'mute',
        // 'volume',
      ],
      ...(fullscreenContainer
        ? { fullscreen: { container: fullscreenContainer } }
        : {}),
      // Plyr defaults to showing the time as a countdown to the end; show
      // elapsed time from 0 instead.
      invertTime: false,
      toggleInvert: false,
      keyboard: { focused: false, global: false },
      // We render our own preview tooltip (see useVideoPlayerChapters), and
      // we don't want Plyr's per-control hover labels either.
      tooltips: { controls: false, seek: false },
      markers: {
        enabled: markerPoints.length > 0,
        points: markerPoints,
      },
    });
    plyrRef.current = player;
    const container = player.elements.container;
    if (container) {
      // Plyr's own `.plyr video { height: 100%; width: 100% }` overrides classes
      // on the <video>, so size the .plyr container instead.
      const videoStyles = twMerge('', videoClassName)
        .split(/\s+/)
        .filter(Boolean);
      if (videoStyles.length) container.classList.add(...videoStyles);
    }
    setPlyrContainer(container ?? null);

    return () => {
      player.destroy();
      plyrRef.current = null;
      setPlyrContainer(null);
      forwardedRef.current = null;
      while (holder.firstChild) holder.removeChild(holder.firstChild);
    };
  }, [videoUrl, videoClassName, videoRef, fullscreenContainer]);

  // Track Plyr buffering state so we can show the spinner while the video
  // is loading frame data or stalled, and hide it once a frame is renderable.
  // `loadedmetadata` is intentionally NOT a "resumed" signal: at that point
  // duration is known (display reads 0:00 → X:XX) but no frame is decoded
  // yet, so the player shows a dark screen — the spinner must remain.
  // `loadeddata` (first frame ready) and `seeked` (post-seek frame ready) are
  // the real "frame visible" signals. `seeking` re-arms the spinner whenever
  // a seek starts (e.g. ResultVideo jumping to its checkpoint startTime).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    // readyState < HAVE_CURRENT_DATA → no decoded frame yet, show spinner.
    setIsVideoBuffering(video.readyState < 2);

    const handleWaiting = () => setIsVideoBuffering(true);
    const handleResumed = () => setIsVideoBuffering(false);

    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('seeking', handleWaiting);
    video.addEventListener('playing', handleResumed);
    video.addEventListener('seeked', handleResumed);
    video.addEventListener('canplay', handleResumed);
    video.addEventListener('loadeddata', handleResumed);

    return () => {
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('seeking', handleWaiting);
      video.removeEventListener('playing', handleResumed);
      video.removeEventListener('seeked', handleResumed);
      video.removeEventListener('canplay', handleResumed);
      video.removeEventListener('loadeddata', handleResumed);
    };
  }, [videoRef, videoUrl]);

  // Forward the loadedmetadata event to the parent. videoUrl is in deps so we
  // re-bind to the new <video> when the URL resolves later (the previous
  // element was destroyed by the Plyr-init effect's cleanup).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onLoadedMetadata) return undefined;

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [onLoadedMetadata, videoRef, videoUrl]);

  return {
    plyrContainer,
    plyrRef,
    playerHolderRef,
    isVideoBuffering,
  };
};

export const useVideoPlayerChapters = ({
  chapters,
  plyrContainer,
  videoRef,
  videoUrl,
}: UseVideoPlayerChaptersProps) => {
  // Keep the latest chapter list in a ref so the listener always reads fresh
  // data without having to re-bind (parents typically recompute `chapters` on
  // every render).
  const chaptersRef = useRef(chapters);
  chaptersRef.current = chapters;

  // Build a YouTube-style hover preview: a small thumbnail of the video at the
  // hovered time, with the active chapter title pinned to the top of the
  // thumbnail and the hover time shown in the bottom-left corner.
  useEffect(() => {
    if (!plyrContainer || !videoUrl) return undefined;

    const progressEl =
      plyrContainer.querySelector<HTMLElement>('.plyr__progress');
    if (!progressEl) return undefined;

    const tooltip = document.createElement('div');
    tooltip.className = 'chapter-preview-tooltip';

    const previewVideo = document.createElement('video');
    previewVideo.className = 'chapter-preview-video';
    previewVideo.crossOrigin = 'anonymous';
    previewVideo.muted = true;
    previewVideo.preload = 'metadata';
    previewVideo.src = videoUrl;
    tooltip.appendChild(previewVideo);

    const titleEl = document.createElement('div');
    titleEl.className = 'chapter-preview-title';
    tooltip.appendChild(titleEl);

    const timeEl = document.createElement('div');
    timeEl.className = 'chapter-preview-time';
    tooltip.appendChild(timeEl);

    plyrContainer.appendChild(tooltip);

    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${String(s).padStart(2, '0')}`;
    };

    const updatePreview = (event: Event) => {
      const me = event as MouseEvent;
      const progressRect = progressEl.getBoundingClientRect();
      if (progressRect.width === 0) return;
      const percent = Math.max(
        0,
        Math.min(1, (me.clientX - progressRect.left) / progressRect.width),
      );
      const duration = videoRef.current?.duration || 0;
      if (!duration) return;
      const hoverTime = percent * duration;

      const chapter = chaptersRef.current?.find(
        (c) => hoverTime >= c.startTime && hoverTime < c.endTime,
      );

      if (chapter?.title) {
        titleEl.textContent = chapter.title;
        titleEl.style.display = '';
      } else {
        titleEl.style.display = 'none';
      }

      timeEl.textContent = formatTime(hoverTime);

      // Seek the preview video to the hovered time so it renders the matching
      // frame. We can only seek once metadata is available (readyState >= 1).
      if (previewVideo.readyState >= 1) {
        previewVideo.currentTime = hoverTime;
      }

      // Follow the cursor horizontally, clamped so the tooltip never overflows
      // the player's bounds.
      const containerRect = plyrContainer.getBoundingClientRect();
      const tooltipWidth = tooltip.offsetWidth || 200;
      const half = tooltipWidth / 2;
      let leftPx = me.clientX - containerRect.left;
      leftPx = Math.max(half, Math.min(containerRect.width - half, leftPx));
      tooltip.style.left = `${leftPx}px`;

      tooltip.classList.add('visible');
    };

    const hidePreview = () => {
      tooltip.classList.remove('visible');
    };

    progressEl.addEventListener('mousemove', updatePreview);
    progressEl.addEventListener('mouseenter', updatePreview);
    progressEl.addEventListener('mouseleave', hidePreview);

    return () => {
      progressEl.removeEventListener('mousemove', updatePreview);
      progressEl.removeEventListener('mouseenter', updatePreview);
      progressEl.removeEventListener('mouseleave', hidePreview);
      if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
    };
  }, [plyrContainer, videoRef, videoUrl]);
};
